import React, { FunctionComponent } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Table } from '../../UI/V3/Table/Table';
import { TableHeader } from '../../UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from '../../UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from '../../UI/V3/Table/Body/TableBody';
import DefaultTableEmptyState from '../../UI/V3/Table/EmptyState/DefaultTableEmptyState';
import { Spinner } from '../../UI/V3/Spinner/Spinner';
import styles from './LocationsTable.module.scss';
import LocationRow from './LocationRow';
import { Location, LocationPagination } from '../../../model/location';
import { useLocationsContext } from '../LocationsProvider';

interface LocationsTableProps {
    locations: Location[];
    pagination?: LocationPagination;
    isLoading?: boolean;
    hasInitialized?: boolean;
    isEmpty?: boolean;
    className?: string;
}

export const LocationsTable: FunctionComponent<LocationsTableProps> = ({
    locations,
    pagination,
    isLoading,
    hasInitialized,
    isEmpty,
    className
}) => {
    const isMobile = useMediaQuery({ query: '(max-width:768px)' });

    const { handlePageChange, handlePageSizeChange } = useLocationsContext();

    // RENDER DIFFERENT TABLE BODIES BASED ON STATE
    const renderTableContent = () => {
        // Show spinner in TableBody when:
        // 1. First load (no data yet)
        // 2. Loading with no current data to show (empty results + loading)
        const shouldShowSpinnerInBody =
            isLoading &&
            (!hasInitialized || // First time loading
                locations.length === 0); // Currently no data to show while loading

        if (shouldShowSpinnerInBody) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={isMobile ? 2 : 7}
                            style={{ textAlign: 'center', padding: '40px' }}
                        >
                            <Spinner />
                        </td>
                    </tr>
                </TableBody>
            );
        }

        // No results for current filters/search
        if (isEmpty) {
            return (
                <TableBody className={styles.tableBodyEmpty}>
                    <DefaultTableEmptyState
                        title="No locations found"
                        description={[
                            'No locations match your current search criteria.',
                            'Try adjusting your filters or search terms.'
                        ]}
                    />
                </TableBody>
            );
        }

        // Normal data
        return (
            <TableBody>
                {locations.map((location, index) => (
                    <LocationRow
                        key={location.id}
                        location={location}
                        isLast={locations.length === index + 1}
                        isMobile={isMobile}
                    />
                ))}
            </TableBody>
        );
    };

    return (
        <Table
            onPageChange={handlePageChange}
            onSizeChange={handlePageSizeChange}
            pagination={pagination}
            className={`${className || ''} ${styles.table}`}
            fullHeight={true}
        >
            <TableHeader className={styles.tableHeader}>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Account</TableHeaderCell>
                <TableHeaderCell>Areas</TableHeaderCell>
                <TableHeaderCell>Address</TableHeaderCell>
                <TableHeaderCell>Phone</TableHeaderCell>
                <TableHeaderCell>Staff</TableHeaderCell>
                <TableHeaderCell></TableHeaderCell>
            </TableHeader>

            {renderTableContent()}
        </Table>
    );
};

export default LocationsTable;
